/**
 * SUBIR Y BAJAR UN ADJUNTO SIN TOPE DE PESO — el lado del navegador.
 *
 * «Por favor que no tengan límite de tamaño para los archivos adjuntos»
 * (usuaria, 2026-08-26). El servidor es `netlify/functions/_lib/subida-partes.ts`
 * y ahí está el porqué con las cifras medidas; en resumen: la plataforma recibe
 * 6 MB de cuerpo, el archivo viaja en base64 (que infla 4/3) y por encima de
 * ~4,4 MB **devuelve un 413 sin cuerpo** — sin JSON, así que la pantalla no
 * tenía ni un mensaje que enseñar y el botón parecía no hacer nada.
 *
 * Lo usan Prospecto, Inactivación, Negociaciones y el Reporte de pago.
 *
 * Información de Interés (db/062) y Catálogos (db/085) tienen su propia copia de
 * este bucle, anterior a este archivo: se dejan como están porque su servidor
 * también es distinto (guarda el avance en la fila del archivo, no en un blob de
 * servicio) y reescribir dos módulos que funcionan para unificar treinta líneas
 * es cambiar riesgo por elegancia.
 *
 * ⚠️ **NO importa nada de `api.ts`, y eso es a propósito.** `api.ts` es la RAÍZ
 * de la capa cliente —no importa ni un módulo local— y sí importa ESTO (para las
 * evidencias de Negociaciones), así que un import de vuelta sería el primer ciclo
 * del proyecto. En su lugar **el POST se inyecta**: cada cliente pasa el suyo en
 * una línea. Por lo mismo no se usa `ApiError` aquí — el 409 se reconoce por su
 * `status` y los errores propios son `Error` normales, que es lo que las
 * pantallas ya saben leer (`ApiError` también extiende `Error`).
 */
import { avisarSiCaducoLaSesion, mensajeDeFallo } from './errores';

/**
 * El trozo de subida: 3 MiB. En base64 son 4 MiB, con margen para el JSON que lo
 * envuelve dentro de los 6 MB de cuerpo; múltiplo de 3 para que cada trozo sea
 * base64 «redondo», sin relleno intermedio.
 *
 * ⚠️ Este número está TAMBIÉN en el servidor (`_lib/subida-partes.ts`), y es la
 * única cifra duplicada de todo esto. Se acepta porque **una discrepancia no
 * puede ser silenciosa**: si esta creciera por encima del máximo que acepta una
 * petición, el servidor responde 422 diciendo exactamente eso («un trozo de
 * subida no puede pasar de 4,40 MB»); y si menguara, solo habría más peticiones.
 * Prospecto además lo recibe del servidor en su catálogo (`trozoBytes`) porque
 * allí ya había uno; los otros tres módulos no tienen catálogo que ampliar.
 */
export const TROZO = 3 * 1024 * 1024;

/** Lo máximo que la plataforma devuelve en UNA respuesta en flujo (20 MB). Por
 *  encima, un adjunto no se puede abrir en una pestaña —el enlace daría el JSON
 *  de un 413— y hay que bajarlo por piezas. */
export const TOPE_UNA_RESPUESTA = 20 * 1024 * 1024;

/** Lee un File (o un trozo suyo) y devuelve su base64 SIN el prefijo dataURL. */
export const archivoABase64 = (b: Blob) => new Promise<string>((res, rej) => {
  const fr = new FileReader();
  fr.onload = () => res(String(fr.result).split(',')[1] ?? '');
  fr.onerror = () => rej(fr.error ?? new Error('No se pudo leer el archivo'));
  fr.readAsDataURL(b);
});

/** El POST del cliente del módulo. Se inyecta (ver la cabecera): `api.post` va
 *  envuelto en una flecha para no perder su `this`. */
export type Poster = <R>(ruta: string, cuerpo: unknown) => Promise<R>;

interface RespuestaParte {
  ok: true;
  /** Solo mientras faltan piezas (202). */
  archivo_ref?: string;
  siguiente?: number;
}

/**
 * Sube UN archivo, en una pieza o en las que hagan falta, y devuelve lo que el
 * módulo responda cuando la subida CIERRA (su soporte, evidencia o recaudo ya
 * con `archivo_ref`).
 *
 * QUIÉN MANDA EN EL ORDEN: el servidor. Cada respuesta dice cuál es la pieza que
 * sigue (`siguiente`) y se le hace caso; si un reintento choca porque esa pieza
 * ya había entrado, el 409 trae `detalle.siguiente` y se retoma por ahí en vez
 * de empezar de cero una subida de 40 MB. `intentos` acota el bucle: sin él, un
 * servidor que devolviera siempre el mismo `siguiente` giraría para siempre.
 */
export async function subirPorPartes<T>(v: {
  /** El POST del cliente que llama (ver `Poster`). */
  post: Poster;
  /** La ruta del endpoint, con su `?seccion=`. */
  ruta: string;
  file: File;
  /** Cómo se llama el archivo en la respuesta del módulo: `soporte`,
   *  `evidencia`, `documento`… */
  clave: string;
  /** Los campos propios del módulo (`negociacion_id`, `pie`, `tipo`…). */
  extra?: Record<string, unknown>;
  trozo?: number;
  onProgreso?: (hechas: number, total: number) => void;
  /** Lo que el módulo devuelva ADEMÁS del archivo, en la respuesta que lo
   *  cierra. Existe desde db/129: al subir el documento de una negociación el
   *  servidor manda el aviso por correo y contesta a quién le llegó, y esa
   *  información se perdía aquí —esta función se queda solo con `r[clave]`—.
   *  Devolver la respuesta entera habría cambiado el tipo de las siete
   *  subidas del portal para que la usara una. */
  onFinal?: (respuesta: Record<string, unknown>) => void;
}): Promise<T> {
  const trozo = v.trozo ?? TROZO;
  const total = Math.max(1, Math.ceil(v.file.size / trozo));
  let ref: string | undefined;
  let i = 0;
  let intentos = 0;

  while (i < total) {
    intentos += 1;
    if (intentos > total + 6) {
      throw new Error(`La subida no avanzó más allá de la parte ${i + 1} de ${total}`);
    }
    const pedazo = v.file.slice(i * trozo, (i + 1) * trozo);
    try {
      const r = await v.post<RespuestaParte & Record<string, unknown>>(v.ruta, {
        ...(v.extra ?? {}),
        nombre: v.file.name,
        base64: await archivoABase64(pedazo),
        /* En una sola pieza no se manda nada de esto: el cuerpo es idéntico al
         * de antes y el servidor lo trata como siempre. */
        ...(total > 1 ? { partes: total, parte: i, bytes_esperados: v.file.size } : {}),
        ...(ref === undefined ? {} : { ref }),
      });
      if (r[v.clave] !== undefined) {
        v.onProgreso?.(total, total);
        v.onFinal?.(r);
        return r[v.clave] as T;
      }
      ref = r.archivo_ref ?? ref;
      i = r.siguiente ?? i + 1;
      v.onProgreso?.(Math.min(i, total), total);
    } catch (e) {
      /* 409 = el servidor y el navegador no coinciden en por dónde iban. Él
       * tiene razón: dice cuál falta y se retoma ahí. Cualquier otro error sube
       * tal cual — un tipo rechazado o un cuadre que no dio no se reintenta. */
      /* Por `status` y no por `instanceof ApiError`: este módulo no importa
       * `api.ts` (ver la cabecera), y el 409 se reconoce igual de bien. */
      const err = e as { status?: number; detalle?: { siguiente?: number } };
      const d = err?.status === 409 ? err.detalle : undefined;
      if (d?.siguiente === undefined || d.siguiente <= i) throw e;
      i = d.siguiente;
      v.onProgreso?.(Math.min(i, total), total);
    }
  }
  /* Se agotaron las piezas sin que el servidor cerrara: no se da por bueno un
   * archivo que él no declaró completo. */
  throw new Error('El servidor no confirmó el archivo completo tras subir todas las partes');
}

/**
 * El fallo de una BAJADA por piezas. Lleva el `status` para quien quiera mirarlo
 * y extiende `Error`, que es lo que las pantallas ya leen: no hace falta que
 * conozcan esta clase para pintar el mensaje.
 */
export class ErrorAdjunto extends Error {
  constructor(readonly status: number, mensaje: string) {
    super(mensaje);
    this.name = 'ErrorAdjunto';
  }
}

/**
 * Baja un adjunto guardado y lo entrega como archivo. Si está por piezas, se
 * piden EN ORDEN y se cosen aquí: los `Blob` se concatenan sin pasar por memoria
 * (el navegador los respalda en disco), que es lo que permite bajar un archivo
 * mayor que la RAM disponible — y esquivar el tope de respuesta de la
 * plataforma, que es de lo que se trata.
 */
export async function descargarPorPartes(v: {
  /** La URL del binario; recibe el índice de la pieza cuando hay varias. */
  url: (parte?: number) => string;
  nombre: string;
  partes: number;
  onProgreso?: (hechas: number, total: number) => void;
}): Promise<void> {
  /* El mensaje se traduce aquí y no se hereda de `api.ts`: este módulo tiene
   * PROHIBIDO importar de allí —`api.ts` importa de él, y sería un ciclo—, así
   * que los dos beben de `errores.ts`, que es hoja de los dos. Lo que sí es
   * común de verdad es el aviso de caducidad: bajar un adjunto con la sesión
   * muerta devuelve al acceso igual que cualquier otra llamada. */
  const traer = async (u: string) => {
    let res: Response;
    try {
      res = await fetch(u, { credentials: 'include' });
    } catch (e) {
      const enLinea = typeof navigator === 'undefined' ? undefined : navigator.onLine;
      throw new ErrorAdjunto(0, mensajeDeFallo(0, e instanceof Error ? e.message : null, { enLinea }));
    }
    if (!res.ok) {
      const cuerpo = await res.json().catch(() => null) as { error?: string; codigo?: string } | null;
      const mensaje = mensajeDeFallo(res.status, cuerpo?.error ?? res.statusText, { codigo: cuerpo?.codigo });
      avisarSiCaducoLaSesion(res.status, cuerpo?.codigo, mensaje);
      throw new ErrorAdjunto(res.status, mensaje);
    }
    return res.blob();
  };

  let contenido: Blob;
  if (v.partes <= 1) {
    contenido = await traer(v.url());
  } else {
    const trozos: Blob[] = [];
    for (let i = 0; i < v.partes; i += 1) {
      trozos.push(await traer(v.url(i)));
      v.onProgreso?.(i + 1, v.partes);
    }
    contenido = new Blob(trozos);
  }
  const url = URL.createObjectURL(contenido);
  const el = document.createElement('a');
  el.href = url;
  el.download = v.nombre;
  el.click();
  /* La URL se suelta CON RETRASO: con un archivo grande el navegador todavía
   * está leyendo el blob cuando el click vuelve, y revocarla ahí mismo cancela
   * la descarga a medias sin ningún error. */
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/** ¿Cabe en UNA respuesta de la plataforma? Si no, no se puede abrir en una
 *  pestaña (el enlace daría el JSON de un 413) y hay que bajarlo por piezas. */
export const cabeEnUnaRespuesta = (
  a: { partes?: number | null; tam_bytes?: number | null },
  tope = TOPE_UNA_RESPUESTA,
) => Number(a.partes ?? 1) <= 1 || Number(a.tam_bytes ?? 0) <= tope;
